import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://api.dev1.auth.joeandalese.com/v1";

const USER_TYPE_LABELS = {
  admin: "Admin",
  contributor: "Contributor",
  view_only: "View Only",
};

function AccountPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Invite form
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [userType, setUserType] = useState("contributor");
  const [inviteStatus, setInviteStatus] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Edit state: { [userId]: newRole }
  const [editRoles, setEditRoles] = useState({});
  const [saving, setSaving] = useState({});
  const [removing, setRemoving] = useState({});

  const navigate = useNavigate();

  const fetchUsers = useCallback(() => {
    setUsersLoading(true);
    fetch(`${API_BASE}/users`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setUsers(data))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { setMe(data); fetchUsers(); })
      .catch(status => { if (status === 401) navigate("/"); })
      .finally(() => setLoading(false));
  }, [navigate, fetchUsers]);

  const handleRoleChange = (userId, newRole) => {
    setEditRoles(prev => ({ ...prev, [userId]: newRole }));
  };

  const handleSaveRole = async (userId) => {
    const newRole = editRoles[userId];
    if (!newRole) return;
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const resp = await fetch(`${API_BASE}/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userType: newRole }),
      });
      if (resp.ok) {
        setUsers(prev => prev.map(u => u.userId === userId ? { ...u, userType: newRole } : u));
        setEditRoles(prev => { const next = { ...prev }; delete next[userId]; return next; });
      }
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRemove = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from your team?`)) return;
    setRemoving(prev => ({ ...prev, [userId]: true }));
    try {
      const resp = await fetch(`${API_BASE}/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (resp.ok) {
        setUsers(prev => prev.filter(u => u.userId !== userId));
      }
    } finally {
      setRemoving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviteStatus(null);
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, phoneNumber: phone, userType }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (resp.status === 401) { navigate("/"); return; }
        setInviteError(data.error || "Failed to send invitation.");
      } else {
        setInviteStatus(`Invitation sent to ${email} as ${USER_TYPE_LABELS[userType]}.`);
        setEmail("");
        setPhone("");
        setUserType("contributor");
        fetchUsers();
      }
    } catch {
      setInviteError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const isAdmin = me?.userType === "admin";

  return (
    <div style={{ maxWidth: 640, margin: "60px auto", padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Account Management</h2>
      <p style={{ color: "#555", marginBottom: 24 }}>
        Signed in as <strong>{me?.email}</strong>
        {" — "}
        <span style={{ color: "#1976d2", fontWeight: 500 }}>{USER_TYPE_LABELS[me?.userType] || me?.userType}</span>
      </p>

      {/* Users table */}
      <h3 style={{ marginBottom: 12 }}>Team Members</h3>
      {usersLoading ? (
        <p style={{ color: "#888" }}>Loading users...</p>
      ) : users.length === 0 ? (
        <p style={{ color: "#888" }}>No users found.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Role</th>
              {isAdmin && <th style={thStyle}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSelf = u.userId === me?.userId;
              const currentRole = editRoles[u.userId] ?? u.userType;
              const isDirty = editRoles[u.userId] !== undefined && editRoles[u.userId] !== u.userType;
              return (
                <tr key={u.userId} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={tdStyle}>
                    {u.email}
                    {isSelf && <span style={{ marginLeft: 6, fontSize: 12, color: "#888" }}>(you)</span>}
                  </td>
                  <td style={tdStyle}>
                    {isAdmin && !isSelf ? (
                      <select
                        value={currentRole}
                        onChange={e => handleRoleChange(u.userId, e.target.value)}
                        style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #ccc" }}
                      >
                        <option value="admin">Admin</option>
                        <option value="contributor">Contributor</option>
                        <option value="view_only">View Only</option>
                      </select>
                    ) : (
                      <span>{USER_TYPE_LABELS[u.userType] || u.userType}</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {!isSelf && (
                        <>
                          <button
                            onClick={() => handleSaveRole(u.userId)}
                            disabled={!isDirty || saving[u.userId]}
                            style={{
                              marginRight: 8, padding: "4px 10px", borderRadius: 4,
                              background: isDirty ? "#1976d2" : "#e0e0e0",
                              color: isDirty ? "#fff" : "#aaa",
                              border: "none", cursor: isDirty ? "pointer" : "default"
                            }}
                          >
                            {saving[u.userId] ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={() => handleRemove(u.userId, u.email)}
                            disabled={removing[u.userId]}
                            style={{
                              padding: "4px 10px", borderRadius: 4,
                              background: "#fff", color: "#d32f2f",
                              border: "1px solid #d32f2f", cursor: "pointer"
                            }}
                          >
                            {removing[u.userId] ? "Removing..." : "Remove"}
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Invite form (admin only) */}
      {isAdmin ? (
        <>
          <h3 style={{ marginBottom: 8 }}>Invite a New User</h3>
          <p style={{ color: "#555", marginBottom: 12 }}>They'll receive an email with a login code.</p>
          <form onSubmit={handleInvite}>
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={inputStyle}
              placeholder="invitee@example.com"
            />
            <label htmlFor="invite-phone">Phone (optional)</label>
            <input
              id="invite-phone"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              style={inputStyle}
              placeholder="10-digit phone number"
            />
            <label htmlFor="invite-role">Role</label>
            <select
              id="invite-role"
              value={userType}
              onChange={e => setUserType(e.target.value)}
              style={inputStyle}
            >
              <option value="admin">Admin</option>
              <option value="contributor">Contributor</option>
              <option value="view_only">View Only</option>
            </select>
            {inviteError && <div style={{ color: "#d32f2f", marginBottom: 12 }}>{inviteError}</div>}
            {inviteStatus && <div style={{ color: "#2e7d32", marginBottom: 12 }}>{inviteStatus}</div>}
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "10px 24px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 4, cursor: submitting ? "not-allowed" : "pointer" }}
            >
              {submitting ? "Sending..." : "Send Invitation"}
            </button>
          </form>
        </>
      ) : (
        <p style={{ color: "#b71c1c" }}>Only admins can invite or manage users.</p>
      )}

      <div style={{ marginTop: 24, textAlign: "center" }}>
        <button onClick={() => navigate("/home")} style={{ background: "none", border: "none", color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}>
          Back to Home
        </button>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 12px",
  fontWeight: 600,
  fontSize: 14,
  color: "#333",
};

const tdStyle = {
  padding: "10px 12px",
  fontSize: 14,
  color: "#333",
};

const inputStyle = {
  width: "100%",
  padding: 8,
  margin: "8px 0 16px 0",
  borderRadius: 4,
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

export default AccountPage;
